import { PageSizeScope } from '@/components/common-table/PageSizeScope'
import { useIsMobile } from '@/hooks/use-mobile'
import type React from 'react'
import { createPortal } from 'react-dom'
import { usePageToolbarExtras } from './HeaderActionsHook'

/**
 * Moves a row a page would stack above its content into the toolbar strip, so collapsing the strip
 * clears everything above the table at once. Wrap the row and leave it where it reads in the page.
 *
 * Renders nothing for the one frame before the strip has mounted its slot, rather than rendering in
 * place and portalling on the next: the move remounts the row, and a search bar inside it would lose
 * what the user had typed. On mobile the strip has no slot and the row stays in the page.
 */
export function ToolbarExtras({
	enabled = true,
	children,
}: {
	/** False for a page component reused inside a panel or bottom bar, where the strip is not its own. */
	enabled?: boolean
	children: React.ReactNode
}) {
	const isMobile = useIsMobile()
	const inStrip = enabled && !isMobile
	const slot = usePageToolbarExtras(inStrip)

	// Scope here too: a page that lays its pager and display popover out by hand, rather than through
	// TableToolbar, still gets the page size moved under Display.
	const scoped = <PageSizeScope>{children}</PageSizeScope>

	if (!inStrip) {
		return scoped
	}
	if (!slot) {
		return null
	}
	return createPortal(scoped, slot)
}
